import { Component, OnInit, Input, ElementRef, AfterContentInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Report } from '../../report/models/report.model';
import { QueryResult } from 'src/app/reports/models/query/query-result.model';
import { QueryService } from 'src/app/reports/services/query.service';
import { QueryPagedRequest } from 'src/app/reports/requests/query-paged.request';
import { QueryParameterRequest } from 'src/app/reports/requests/query-parameter.request';
import { Column } from '../../report/models/column.model';
import { RowResult } from './models/row-result.model';
import { ReportSettings, OrientationEnum } from '../report-settings/models/reportsettings.model';
import { RowDetail } from './models/row-detail.model';
import { Filter } from '../../report/models/filter.model';
import { ICallback } from 'src/app/reports/models/Icallback.model';
import { RTEAlertService, RteDialog } from 'rte-library';
import { BaseComponent } from 'src/app/reports/shared/utils/base.component';
import { getRandomId } from 'src/app/reports/helpers/data.helper';
import { ReportRender } from 'src/app/models/reportRender';
import { ReportErrorExportDialogComponent } from './report-error-export-dialog/report-error-export-dialog.component';
import { getAllColumns, isReporGrouped } from 'src/app/reports/helpers/column.helper';
import { getExportQueryParameter, getQueryParameter, getQueryParameterList } from 'src/app/reports/helpers/query-parameter.helper';
import { getAllFilters } from 'src/app/reports/helpers/filter.helper';

@Component({
  selector: 'app-report-render',
  templateUrl: './report-render.component.html',
  styleUrls: ['./report-render.component.scss']
})
export class ReportRenderComponent extends BaseComponent implements OnInit, AfterContentInit {

  //#region Inputs

  @Input() report: Report;
  @Input() parameters: Array<QueryParameterRequest>;
  @Input() settings: ReportSettings;
  @Input() showReport: ReportRender;

  //#endregion

  //#region Properties

  allColumns: Array<Column> = [];
  page = 1;
  totalPage: number;
  total: number;
  rowResults: Array<RowResult> = new Array<RowResult>();
  landscape: OrientationEnum = OrientationEnum.Landscape;
  portrait: OrientationEnum = OrientationEnum.Portrait;
  columnsMaxLength = new Map<string,number>();
  reportHeight: string;
  disabledSearchNextRows: boolean = false;


  @Output() loading: EventEmitter<ReportRenderComponent> = new EventEmitter<ReportRenderComponent>();


  //#endregion

  //#region Ctor

  constructor(private queryService: QueryService,
    private alert: RTEAlertService,
    public elm: ElementRef,
    public dialog: RteDialog) {
      super();
    }

  //#endregion

  //#region Interface Implementations

  ngOnInit() {
    this.allColumns = getAllColumns(this.report);
    this.setReportHeight();
    this.getQueryResult(this.page);
  }

  ngAfterContentInit(): void {
    setTimeout(_ => this.loadingComponent());
  }

  loadingComponent() {
    if (this) {
      this.loading.emit(this);
    }
  }

  //#endregion

  //#region Query Result

  getQueryResult(page: number, row: RowResult = null, callback: ICallback = null) {
    const isDetail: boolean = !!row,
      obj: QueryPagedRequest = this.getQueryPagedRequestInstance(isDetail, page, row);

    if (!obj.Query) {
      return;
    }

    this.subscription.add(
      this.queryService.queryResultWithTimeout(obj).subscribe(result => {

        if (this.validQueryResult(result)) {
          return;
        }

        let list = [];

        if (isDetail) {
          list = row.Rows;
        } else {
          list = this.rowResults;
          this.totalPage = result.AmountPage === 0 && result.Rows.length > 0 ?  1 : result.AmountPage;
          this.total = result.Total;
        }
        this.boundQueryResult(result, page, isDetail, list);
        this.setReportHeight();

        if (result.Rows.length == 0) {
          this.page--;
          this.disabledSearchNextRows = true;
          this.alert.info('Todos os registros foram carregados.')
        }

        if (callback) {
          callback.callback();
        }
      }, error => {
        this.openDialog();
        this.dialogAfterClose();
      })
    );
  }

  openDialog() {
    const contentBefore = `<div class="header-dialog-export"><p>Ops...</p></div>
                          <p>
                            Infelizmente não conseguimos processar em tempo hábil essa consulta com os filtros escolhidos por você.
                          </p>
                          <p>
                            Porém, não vamos te deixar na mão! Iremos exportar seu relatório, basta selecionar a extensão desejada.
                          </p>`;
    const contentAfter = `<p>
                            Fique tranquilo que vamos te notificar assim que concluirmos. 😉
                          </p>`;

    this.dialog.open(ReportErrorExportDialogComponent, {
      width: '50vw',
      minHeight: '30vh',
      data: {
        report: this.report,
        totalPage: this.totalPage,
        settings: this.settings,
        columns: this.allColumns,
        parameters: this.parameters,
        contentBeforeComponent: contentBefore,
        contentAfterComponent: contentAfter,
      }
    });
  }

  dialogAfterClose() {
    this.dialog.afterAllClosed.subscribe(result => {
      this.showReport.showReportRender = false;
      this.showReport.showReportView = true;
    });
  }

  validQueryResult(result: QueryResult) {
    if (!result.Success) {
      this.alert.error(result.Message);
    }
    return !result.Success;
  }

  getCurrentRows(): Array<RowResult> {
    return this.rowResults.filter(value => value.Page === this.page);
  }

  getRowResults(): Array<RowResult> {
    return this.rowResults.filter(value => value.Page <= this.page);
  }

  boundQueryResult(result: QueryResult, page: number, isDetail: boolean, rows: Array<RowResult>) {
    let isGroup = isReporGrouped(this.report);

    result.Rows.forEach(element => {
      const data: RowResult = {
        AmountPage: result.AmountPage,
        Id: getRandomId(),
        Page : page,
        Rows : [],
        Data: element,
        IsGroup : !isDetail && isGroup,
        IsDetail: isDetail,
        Index: result.Rows.indexOf(element),
        Columns: isDetail || isGroup == false ? this.getDetailcolumns() : this.getGroupColumns(),
        Total: result.Total
      };

      rows.push(data);
    });
  }

  getQueryPagedRequestInstance(isDetail: boolean, page: number, row: RowResult): QueryPagedRequest {
    const parameterList = getQueryParameterList(this.parameters, getAllFilters(this.report));
    return {
      AmountByPage: isDetail ?  this.settings.PageSizeDetail : this.settings.PageSize,
      Page : page,
      ProviderId : this.report.Provider.Id,
      Query: isDetail ? this.report.QueryDetail : this.report.Query ,
      Parameters: getQueryParameter(this.report ,parameterList, row)
    };
  }

  searchNextRows() {
    this.page++;
    this.getQueryResult(this.page);
  }

  //#endregion

  //#region Report Infos



  getDetailcolumns(): Array<Column> {
    return this.allColumns.filter(value => !value.IsGroupBy);
  }

  getGroupColumns(): Array<Column> {
    return this.allColumns.filter(value => value.IsGroupBy);
  }

  getAllGroupColumns():  Array<Filter> {
    const filters: Array<Filter> = [];

    this.report.Entities.forEach(entity => {
      for (const item of Object.keys(entity.Filters)) {
        filters.push(entity.Filters[item]);
      }
    });

    return filters;
  }

  getAllFilters(): Array<Filter> {
    const filters: Array<Filter> = [];

    this.report.Entities.forEach(entity => {

      for (const item of Object.keys(entity.Filters)) {
        filters.push(entity.Filters[item]);
      }
    });

    return filters;
  }

  getFilterByName(name: string ): Filter {
    return this.getAllFilters().find(x => x.Name === name);
  }

  //#endregion

  //#region Events

  getDetailRow(detail: RowDetail) {
    if (detail.Row) {
      this.getQueryResult(detail.Page, detail.Row);
    }
  }

  pageChange(pageChange: number = 1) {
    this.page = pageChange;
    const current = this.getCurrentRows();

    if (current.length === 0) {
      this.getQueryResult(pageChange);
    }
  }
  //#endregion

  //#region Visual

  isHeaderVisible(): boolean {
    return this.settings.PageSize && this.totalPage > 0;
  }

  setReportHeight() {
    const rowHeight = 28;
    const headerHeight = 40;
    let amountByPage = this.settings.PageSize;

    if (this.reportHeight != undefined && this.settings.PageSize > this.rowResults.length)
      amountByPage = this.rowResults.length;

    this.reportHeight = (amountByPage * rowHeight + headerHeight).toString() + 'px';
  }

  //#endregion
}
